import axiosInstance from "api"
import { Endpoint } from "api/enpoints.types"
import { AxiosResponse, AxiosError } from "axios"
import qs from "qs"
import { useInfiniteQuery, useQuery } from "react-query"
import { IStudentDetailed } from "ts/interfaces/Student"

import {
  ClassRosterResponse,
  IGetClassesStudentsArgs,
  IGetClassesStudentsRosterArgs,
  IInfiniteQueryClassesStudentsArgs,
} from "./classesStudents.types"

export const classesStudentsQueryKey = (
  classId: number,
  filters?: {
    page?: number
    limit?: number
    sorting?: string | null
    sort_by?: string | null
    filter?: string
    group_ids?: number[] | null
  },
) => [
  "classesStudents",
  classId,
  ...(filters
    ? [
        filters.page,
        filters.limit,
        filters.sorting,
        filters.sort_by,
        filters.filter,
        filters.group_ids,
      ]
    : []),
]

export const useQueryClassesStudents = ({
  classId,
  options,
  page,
  sorting,
  filter,
  sort_by,
  limit,
  group_ids,
}: IGetClassesStudentsArgs) =>
  useQuery<AxiosResponse<IStudentDetailed>, AxiosError>(
    classesStudentsQueryKey(classId, {
      page,
      limit,
      sorting,
      sort_by,
      filter,
      group_ids,
    }),
    async () =>
      axiosInstance(Endpoint.CLASSES_ID_STUDENTS.replace("ID", `${classId}`), {
        params: {
          skip: page,
          limit,
          sorting,
          sort_by,
          filter: filter && filter?.length > 0 ? filter : null,
          group_ids: group_ids?.length ? group_ids : undefined,
        },
        paramsSerializer: (params) =>
          qs.stringify(params, { indices: false, skipNulls: true }),
      }),
    options,
  )

export const useInfiniteQueryClassesStudents = ({
  classId,
  filter,
  limit = 30,
  options,
}: IInfiniteQueryClassesStudentsArgs) =>
  useInfiniteQuery<AxiosResponse<IStudentDetailed>, AxiosError>(
    ["classesStudents", classId, "infinite", limit, filter],
    async ({ pageParam = 0 }) =>
      axiosInstance(Endpoint.CLASSES_ID_STUDENTS.replace("ID", `${classId}`), {
        params: {
          skip: pageParam,
          limit,
          filter: filter && filter.length > 0 ? filter : null,
        },
        paramsSerializer: (params) =>
          qs.stringify(params, { indices: false, skipNulls: true }),
      }),
    {
      ...options,
      getNextPageParam: (lastPage, allPages) => {
        const loadedItemCount = allPages.reduce(
          (acc, page) => acc + page.data.objects.length,
          0,
        )
        const totalItems = lastPage.data.all_objects_count

        if (loadedItemCount < totalItems) {
          return loadedItemCount
        }
        return undefined
      },
    },
  )

export const useQueryHelpUpdateClassesStudents = ({
  classId,
  options,
  page,
  sorting,
  filter,
  sort_by,
  limit,
}: IGetClassesStudentsArgs) =>
  useQuery<AxiosResponse<IStudentDetailed>, AxiosError>(
    ["classesStudentsCopy", classId],
    async () =>
      axiosInstance(Endpoint.CLASSES_ID_STUDENTS.replace("ID", `${classId}`), {
        params: {
          skip: page,
          limit,
          sorting,
          sort_by,
          filter: filter && filter?.length > 0 ? filter : null,
        },
      }),
    options,
  )

// Backend page-size cap for GET /classes/{id}/students (and the banker helper).
export const CLASS_ROSTER_LIMIT = 200

// Whole roster in one page (spec max 200) under its own key — never overwrites the paginated table cache.
export const useQueryClassesStudentsRoster = ({
  classId,
  source = "teacher",
  options,
}: IGetClassesStudentsRosterArgs) =>
  useQuery<AxiosResponse<ClassRosterResponse>, AxiosError>(
    ["classesStudentsRoster", classId, source],
    async () =>
      axiosInstance(
        (source === "banker"
          ? Endpoint.CLASSES_ID_HELPER_BANKER_STUDENTS
          : Endpoint.CLASSES_ID_STUDENTS
        ).replace("ID", `${classId}`),
        { params: { skip: 0, limit: CLASS_ROSTER_LIMIT } },
      ),
    options,
  )
